import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { Users, Plus, Edit, Trash2, Lock, Unlock, X, Save, RefreshCw } from 'lucide-react'
import api from '../api'

const EMPTY = { username:'', password:'', fullname:'', email:'', role:'user', active:true }

export default function AdminPanel() {
  const [users,   setUsers]   = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editId,  setEditId]  = useState(null)
  const [form,    setForm]    = useState(EMPTY)
  const [saving,  setSaving]  = useState(false)

  const load = async () => {
    setLoading(true)
    try { const r = await api.get('/admin/users'); setUsers(r.data) }
    catch { toast.error('Không tải được danh sách người dùng') }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const openCreate = () => { setEditId(null); setForm(EMPTY); setShowModal(true) }

  const openEdit = (u) => {
    setEditId(u.id)
    setForm({ username:u.username, password:'', fullname:u.fullname||'', email:u.email||'', role:u.role, active:!!u.active })
    setShowModal(true)
  }

  const save = async () => {
    if (!form.username) return toast.error('Vui lòng nhập tên đăng nhập')
    if (!editId && !form.password) return toast.error('Vui lòng nhập mật khẩu')
    setSaving(true)
    try {
      if (editId) { await api.put(`/admin/users/${editId}`, form); toast.success('Cập nhật thành công') }
      else        { await api.post('/admin/users', form);           toast.success('Tạo tài khoản thành công') }
      setShowModal(false)
      load()
    } catch (e) { toast.error(e.response?.data?.error || 'Lỗi khi lưu') }
    finally { setSaving(false) }
  }

  const toggleLock = async (u) => {
    try {
      // FIX: gửi đầy đủ fields required bởi backend
      await api.put(`/admin/users/${u.id}`, {
        role:     u.role,
        active:   !u.active,   // toggle
        fullname: u.fullname || '',
        email:    u.email    || '',
      })
      toast.success(u.active ? 'Đã khóa tài khoản' : 'Đã mở khóa tài khoản')
      load()
    } catch (e) { toast.error(e.response?.data?.error || 'Lỗi cập nhật') }
  }

  const del = async (u) => {
    if (!window.confirm(`Xóa tài khoản "${u.username}"? Hành động này không thể hoàn tác.`)) return
    try { await api.delete(`/admin/users/${u.id}`); toast.success('Đã xóa tài khoản'); load() }
    catch (e) { toast.error(e.response?.data?.error || 'Lỗi khi xóa') }
  }

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }))

  return (
    <div className="space-y-5">
      {/* Title + actions */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
            background:'linear-gradient(135deg,#8b5cf6,#6d28d9)',
            display:'flex', alignItems:'center', justifyContent:'center' }}>
            <Users size={20} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize:18, fontWeight:700 }}>Quản lý người dùng</h1>
            <p style={{ fontSize:13, color:'var(--text3)' }}>{users.length} tài khoản</p>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-ghost" onClick={load} title="Tải lại"><RefreshCw size={14} /></button>
          <button className="btn btn-primary" onClick={openCreate}><Plus size={14} /> Thêm tài khoản</button>
        </div>
      </div>

      {/* Table */}
      <div className="grad-border" style={{ overflow:'hidden' }}>
        {loading ? (
          <div style={{ textAlign:'center', padding:40, color:'var(--text3)', fontSize:14 }}>Đang tải...</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--border)' }}>
                  {['Tên đăng nhập','Họ tên','Email','Vai trò','Trạng thái','Ngày tạo',''].map(h => (
                    <th key={h} style={{ textAlign:'left', padding:'10px 14px', fontSize:12,
                      fontWeight:600, color:'var(--text3)', textTransform:'uppercase', letterSpacing:.5 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom:'1px solid rgba(31,48,80,.5)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ fontFamily:'JetBrains Mono,monospace', fontSize:14, fontWeight:700, color:'var(--accent)' }}>
                        {u.username}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:14, color:'var(--text2)' }}>{u.fullname || '—'}</td>
                    <td style={{ padding:'10px 14px', fontSize:13, color:'var(--text3)', fontFamily:'JetBrains Mono,monospace' }}>{u.email || '—'}</td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700,
                        background: u.role==='admin' ? 'rgba(56,189,248,.15)' : 'var(--panel2)',
                        color:      u.role==='admin' ? 'var(--accent)'        : 'var(--text3)',
                        border:     u.role==='admin' ? '1px solid rgba(56,189,248,.3)' : '1px solid var(--border)' }}>
                        {u.role==='admin' ? '👑 Admin' : '👤 User'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:700,
                        background: u.active ? 'rgba(52,211,153,.15)' : 'rgba(248,113,113,.15)',
                        color:      u.active ? '#34d399'               : '#f87171',
                        border:     u.active ? '1px solid rgba(52,211,153,.3)' : '1px solid rgba(248,113,113,.3)' }}>
                        {u.active ? '✅ Hoạt động' : '🔒 Bị khóa'}
                      </span>
                    </td>
                    <td style={{ padding:'10px 14px', fontSize:12, color:'var(--text3)' }}>
                      {new Date(u.created_at).toLocaleDateString('vi-VN')}
                    </td>
                    <td style={{ padding:'10px 14px' }}>
                      <div style={{ display:'flex', gap:4 }}>
                        <button onClick={() => openEdit(u)} title="Chỉnh sửa"
                          style={{ padding:6, borderRadius:6, background:'none', border:'none',
                            cursor:'pointer', color:'var(--accent)' }}>
                          <Edit size={14} />
                        </button>
                        <button onClick={() => toggleLock(u)}
                          title={u.active ? 'Khóa tài khoản' : 'Mở khóa'}
                          disabled={u.username==='admin'}
                          style={{ padding:6, borderRadius:6, background:'none', border:'none',
                            cursor: u.username==='admin' ? 'not-allowed' : 'pointer',
                            color: u.active ? '#fbbf24' : '#34d399',
                            opacity: u.username==='admin' ? .4 : 1 }}>
                          {u.active ? <Lock size={14} /> : <Unlock size={14} />}
                        </button>
                        <button onClick={() => del(u)} title="Xóa"
                          disabled={u.username==='admin'}
                          style={{ padding:6, borderRadius:6, background:'none', border:'none',
                            cursor: u.username==='admin' ? 'not-allowed' : 'pointer',
                            color:'var(--danger)',
                            opacity: u.username==='admin' ? .4 : 1 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign:'center', padding:40, color:'var(--text3)', fontSize:14 }}>
                    Chưa có người dùng nào
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-backdrop" onClick={e => e.target===e.currentTarget && setShowModal(false)}>
          <div className="modal-box">
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ fontSize:16, fontWeight:700, color:'var(--accent)' }}>
                {editId ? '✏️ Chỉnh sửa tài khoản' : '➕ Tạo tài khoản mới'}
              </h3>
              <button onClick={() => setShowModal(false)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:4 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Tên đăng nhập *</label>
                <input className="inp" placeholder="username" value={form.username}
                  onChange={e => setF('username', e.target.value)} disabled={!!editId} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>
                  Mật khẩu {editId && <span style={{ color:'var(--text3)', fontWeight:400 }}>(để trống = không đổi)</span>}
                  {!editId && ' *'}
                </label>
                <input className="inp" type="password" placeholder="••••••••" value={form.password}
                  onChange={e => setF('password', e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Họ tên</label>
                <input className="inp" placeholder="Nguyễn Văn A" value={form.fullname}
                  onChange={e => setF('fullname', e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Email</label>
                <input className="inp" type="email" placeholder="email@example.com" value={form.email}
                  onChange={e => setF('email', e.target.value)} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Vai trò</label>
                  <select className="inp" value={form.role} onChange={e => setF('role', e.target.value)}>
                    <option value="user">👤 User</option>
                    <option value="admin">👑 Admin</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:13, color:'var(--text3)', marginBottom:6 }}>Trạng thái</label>
                  <select className="inp" value={form.active ? '1' : '0'}
                    onChange={e => setF('active', e.target.value === '1')}>
                    <option value="1">✅ Hoạt động</option>
                    <option value="0">🔒 Bị khóa</option>
                  </select>
                </div>
              </div>
              <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center', marginTop:4 }}
                onClick={save} disabled={saving}>
                <Save size={14} /> {saving ? 'Đang lưu...' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
